import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { map } from 'rxjs/operators';
import { JsonConvert } from 'json2typescript';
import { PerEmployeeCardsModel } from '../model/per-employee-cards-model';
import { PerEmployeeCardsQueryModel } from '../model/per-employee-cards-query-model';
import { HrConfigurationService } from '../../service/hr.configuration.service';
import { AuthorizedEmployeeCardModel } from '../model/authorized-employee-card-model';

@Injectable({
    providedIn: 'root',
})
export class PerEmployeeCardsService {
    endpoint: string;
    readonly serviceRootPattern = '/perEmployeeCardsService/perEmployeeCards/';

    constructor(
        private httpClient: HttpClient,
        private configService: HrConfigurationService,
    ) {
        this.endpoint = configService.config.hr.general;
    }

    public delete(id: number): Observable<any> {
        const pattern = '/perEmployeeCardsService/perEmployeeCards/' + id;
        return this.httpClient.delete(this.endpoint + pattern);
    }

    public queryPage(pagination: PerEmployeeCardsQueryModel): Observable<HvlPage<Array<PerEmployeeCardsModel>>> {
        const pattern = this.serviceRootPattern + 'page';
        return this.httpClient
            .post<HvlResponse<HvlPage<PerEmployeeCardsModel[]>>>(this.endpoint + pattern, pagination)
            .pipe(
                map((hvlpage) => {
                    if (hvlpage.body?.totalElements === 0) {
                        return new HvlPage<Array<PerEmployeeCardsModel>>(
                            [],
                            hvlpage.body.totalElements,
                            hvlpage.body.totalPages,
                            hvlpage.errorDetail,
                        );
                    }

                    const converter = new JsonConvert();
                    const model = converter.deserializeArray(hvlpage.body.data, PerEmployeeCardsModel);
                    return new HvlPage<Array<PerEmployeeCardsModel>>(
                        model,
                        hvlpage.body.totalElements,
                        hvlpage.body.totalPages,
                        hvlpage.errorDetail,
                    );
                }),
            );
    }

    public getById(id: number): Observable<PerEmployeeCardsModel> {
        const url = `${this.endpoint}${this.serviceRootPattern}${id}`;
        return this.httpClient.get<HvlResponse<PerEmployeeCardsModel>>(url).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerEmployeeCardsModel);
            }),
        );
    }

    public update(model: PerEmployeeCardsModel): Observable<PerEmployeeCardsModel> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerEmployeeCardsModel>>(
                this.endpoint + pattern,
                converter.serialize(model, PerEmployeeCardsModel),
            )
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeObject(data.body, PerEmployeeCardsModel);
                }),
            );
    }

    public create(model: PerEmployeeCardsModel): Observable<number> {
        const pattern = this.serviceRootPattern;
        const converter = new JsonConvert();

        return this.httpClient
            .post<HvlResponse<number>>(this.endpoint + pattern, converter.serialize(model, PerEmployeeCardsModel))
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public queryList(queryModel: PerEmployeeCardsQueryModel): Observable<Array<PerEmployeeCardsModel>> {
        const pattern = this.serviceRootPattern + 'query';
        return this.httpClient
            .post<HvlResponse<Array<PerEmployeeCardsModel>>>(this.endpoint + pattern, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerEmployeeCardsModel);
                }),
            );
    }

    public getAuthorizedEmployeeCardsOfSessionPersonnel(
        personnelId: Number,
    ): Observable<Array<AuthorizedEmployeeCardModel>> {
        const pattern =
            this.configService.config.hr.personnel +
            '/personnelService/personnel/getAuthorizedEmployeeCardsOfSessionPersonnel/' +
            personnelId;
        return this.httpClient.get<HvlResponse<Array<AuthorizedEmployeeCardModel>>>(pattern).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], AuthorizedEmployeeCardModel);
            }),
        );
    }

    public getAuthorizedEmployeeCardsOfSessionPersonnelWithCheckCardUsage(
        personnelId: Number,
    ): Observable<Array<AuthorizedEmployeeCardModel>> {
        const pattern =
            this.configService.config.hr.personnel +
            '/personnelService/personnel/getAuthorizedEmployeeCardsOfSessionPersonnelWithCheckCardUsage/' +
            personnelId;
        return this.httpClient.get<HvlResponse<Array<AuthorizedEmployeeCardModel>>>(pattern).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], AuthorizedEmployeeCardModel);
            }),
        );
    }

    public addCardImage(file: File, cardId: number): Observable<String> {
        let formData: FormData = new FormData();
        formData.append('cardImage', file, file.name);

        let headers = new HttpHeaders();
        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');

        return this.httpClient
            .post<HvlResponse<String>>(this.endpoint + this.serviceRootPattern + 'addCardImage/' + (cardId ?? ''), formData, { headers: headers })
            .pipe(
                map((data) => {
                    return data.body;
                }),
            );
    }

    public getCardImage(cardId: number): string {
        return this.endpoint + this.serviceRootPattern + 'getCardImage/' + cardId;
    }

    public checkIfRelatedWithPayroll(id: number): Observable<Boolean> {

        const pattern = this.endpoint + this.serviceRootPattern + 'checkIfRelatedWithPayroll/' + id;

        return this.httpClient.get<HvlResponse<Boolean>>(pattern).pipe(
            map((data) => {
                return data.body;
            }),
        );
    }

    public getEmployeeCardAuthorityRoleCodes(): Observable<Array<string>> {
        const pattern = this.serviceRootPattern + 'getEmployeeCardAuthorityRoleCodes';
        return this.httpClient
            .get<HvlResponse<Array<string>>>(this.endpoint + pattern)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return data.body || [];
                }),
            );
    }
}
